import React, { useState, useEffect, useContext } from "react";
import { Form, Input, Button, Table, message, Select, Spin, Modal } from "antd";
import axios from "axios";
import SummaryApi from "../../common/index";
import { UserContext } from "../../context/UserContext";

const { TextArea } = Input;
const { Option } = Select;

const UserQueriesPage = () => {
  const { user } = useContext(UserContext);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raising, setRaising] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch all queries
  const fetchUserQueries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(SummaryApi.getMyUserQueries.url);
      if (res.data.success) setQueries(res.data.queries);
    } catch (err) {
      console.error("Error fetching queries:", err);
      message.error("Failed to fetch your queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserQueries();
  }, []);

  const onFinish = async (values) => {
    try {
      setRaising(true);
      const res = await axios.post(SummaryApi.raiseUserToAdminQuery.url, values);
      if (res.data.success) {
        message.success(res.data.message);
        fetchUserQueries();
      } else {
        message.error(res.data.message || "Failed to raise query");
      }
    } catch (err) {
      console.error("Error raising query:", err);
      message.error(err.response?.data?.message || "Failed to raise query");
    } finally {
      setRaising(false);
    }
  };

  const fetchQueryDetails = async (queryId) => {
    try {
      setLoading(true);
      const res = await axios.get(SummaryApi.getQueryDetailsToUser(queryId).url);
      if (res.data.success) {
        setSelectedQuery(res.data.query);
        setMessages(res.data.messages || []);
        setModalVisible(true);
      }
    } catch (err) {
      console.error("Error fetching query details:", err);
      message.error("Failed to fetch query details");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!reply) return message.warning("Please enter a message to reply");
    try {
      const res = await axios.post(SummaryApi.addMessage.url, {
        query_id: selectedQuery.id,
        message: reply,
      });
      if (res.data.success) {
        message.success("Reply sent successfully");
        setMessages([...messages, { sender_role: "user", message: reply, created_at: new Date() }]);
        setReply("");
      } else {
        message.error(res.data.message || "Failed to send reply");
      }
    } catch (err) {
      console.error("Error sending reply:", err);
      message.error("Failed to send reply");
    }
  };

  const columns = [
    { title: "Subject", dataIndex: "subject", key: "subject" },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    { title: "Status", dataIndex: "status", key: "status" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => fetchQueryDetails(record.id)}>
          View & Reply
        </Button>
      ),
    },
  ];

  return (
  <div style={{ padding: 20, width: "100%", boxSizing: "border-box" }}>
      <h2>User Queries</h2>

      {/* Raise new query */}
      <Form
        layout="vertical"
        onFinish={onFinish}
        style={{
          marginBottom: "30px",
          background: "#fff",
          padding: "20px",
          borderRadius: 8,
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      >
        <h3>Raise a New Admin Query</h3>
        <Form.Item label="Subject" name="subject" rules={[{ required: true, message: "Please enter a subject" }]}>
          <Input placeholder="Enter subject" />
        </Form.Item>

        <Form.Item label="Description" name="description" rules={[{ required: true, message: "Please enter a description" }]}>
          <TextArea rows={4} placeholder="Enter description" />
        </Form.Item>

        <Form.Item label="Category" name="category">
          <Select placeholder="Select category" allowClear>
            <Option value="order">Order</Option>
            <Option value="product">Product</Option>
            <Option value="payment">Payment</Option>
            <Option value="other">Other</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Priority" name="priority">
          <Select placeholder="Select priority" allowClear>
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={raising}>
            Raise Query
          </Button>
        </Form.Item>
      </Form>

      {/* Responsive Table / Card */}
      {loading ? (
    <Spin />
  ) : isMobile ? (
    queries.map((q) => (
      <div key={q.query_id} style={{
        border: "1px solid #eee",
        borderRadius: 8,
        padding: 15,
        marginBottom: 15,
        background: "#fff",
        boxShadow: "0 0 5px rgba(0,0,0,0.1)",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <p><b>Subject:</b> {q.subject}</p>
        <p><b>Category:</b> {q.category}</p>
        <p><b>Priority:</b> {q.priority}</p>
        <p><b>Status:</b> {q.status}</p>
        <Button type="link" onClick={() => fetchQueryDetails(q.id)}>View & Reply</Button>
      </div>
    ))
  ) : (
    <div style={{ overflowX: "auto", width: "100%" }}>
      <Table
        dataSource={queries}
        columns={columns}
        rowKey="query_id"
        pagination={{ pageSize: 5 }}
        scroll={{ x: "max-content" }}
      />
    </div>
  )}

  {/* Modal */}
  <Modal
    title={`Query Details: ${selectedQuery?.subject}`}
    visible={modalVisible}
    onCancel={() => setModalVisible(false)}
    footer={null}
    width="90%"   // responsive for mobile
    style={{ top: 20 }}
  >
    {selectedQuery && (
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <p><b>Category:</b> {selectedQuery.category}</p>
        <p><b>Priority:</b> {selectedQuery.priority}</p>
        <p><b>Status:</b> {selectedQuery.status}</p>
        <p><b>Description:</b> {selectedQuery.description}</p>

        <hr />
        <h4>Messages</h4>
        <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 10 }}>
          {messages.map((msg, index) => (
            <div key={index} style={{ marginBottom: 8 }}>
              <b>{msg.sender_role === "admin" ? "Admin" : "You"}:</b> {msg.message}
              <span style={{ fontSize: 12, color: "#888" }}> ({new Date(msg.created_at).toLocaleString()})</span>
            </div>
          ))}
        </div>

        <TextArea rows={3} placeholder="Write a reply..." value={reply} onChange={(e) => setReply(e.target.value)} />
        <Button type="primary" style={{ marginTop: 10 }} onClick={handleSendReply}>
          Send Reply
        </Button>
      </div>
    )}
  </Modal>
    </div>
  );
};

export default UserQueriesPage;
